import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { SocialButton } from "../components/SocialButton";
import { useAuthActions } from "../hooks/auth/useAuthActions";
import MainLogo from "../components/Logo";
import designTokens from "../styles/designTokens";
import { useAuth } from "../contexts/AuthContext";
import Privacy from "../components/Privacy";
import Terms from "../components/Terms";
import {
  completePairingSession,
  DEVICE_PAIRING_TTL_MS,
  fetchPairingStatus,
} from "../services/devicePairingClient";
import {
  clearDesktopPairingAuthAttempt,
  isDesktopSocialPairingFlow,
  parseDesktopSocialProvider,
  readDesktopPairingAuthAttempt,
  writeDesktopPairingAuthAttempt,
  type DesktopPairingAuthAttempt,
} from "../services/desktopPairingAuth";

const dividerLineSx = {
  flex: 1,
  height: "1px",
  backgroundColor: designTokens.fill.text.grey80.dark,
  opacity: 0.35,
  maxWidth: 215,
};

/**
 * `/pair` — Vision Pro 연동·스튜디오 코드 플로우 (모든 클라이언트).
 * 디자인: Figma View-R (AVP 로그인 / Studio Code).
 */
const PairPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    error: authError,
    redirectSignInResult,
  } = useAuth();
  const { loginGoogle, loginApple, loginMicrosoft, loginGuest } =
    useAuthActions();

  const sidFromUrl = searchParams.get("session");
  const codeFromUrl = searchParams.get("code");
  const desktopProvider = parseDesktopSocialProvider(
    searchParams.get("provider"),
  );
  const hasSessionParams = Boolean(sidFromUrl && codeFromUrl);
  const isDesktopProviderFlow =
    hasSessionParams &&
    isDesktopSocialPairingFlow(searchParams.get("flow"), desktopProvider);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [shortCode, setShortCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pairComplete, setPairComplete] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);
  const [desktopAuthAttempt, setDesktopAuthAttempt] =
    useState<DesktopPairingAuthAttempt | null>(() =>
      sidFromUrl ? readDesktopPairingAuthAttempt(sidFromUrl) : null,
    );
  const [launchingDesktopAuth, setLaunchingDesktopAuth] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const completeOnce = useRef(false);
  const sessionBootstrapped = useRef(false);
  const desktopAuthLaunchAttempted = useRef(false);
  const completeRunGenerationRef = useRef(0);
  const activeCompleteSessionRef = useRef<string | null>(null);

  const cancelCompleteLoop = useCallback(() => {
    completeRunGenerationRef.current += 1;
    activeCompleteSessionRef.current = null;
  }, []);

  useEffect(() => {
    if (sessionBootstrapped.current) return;
    sessionBootstrapped.current = true;

    const sid = sidFromUrl;
    const code = codeFromUrl;
    if (sid && code) {
      setSessionId(sid);
      setShortCode(code);
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [sidFromUrl, codeFromUrl]);

  const launchDesktopProviderAuth = useCallback(async () => {
    if (!sessionId || !desktopProvider) return;

    const attempt = writeDesktopPairingAuthAttempt(
      sessionId,
      desktopProvider,
    );
    setDesktopAuthAttempt(attempt);
    setPairError(null);
    setLaunchingDesktopAuth(true);

    try {
      if (desktopProvider === "google") {
        await loginGoogle();
      } else if (desktopProvider === "microsoft") {
        await loginMicrosoft();
      } else {
        await loginApple();
      }
    } catch (error) {
      setPairError(
        error instanceof Error
          ? error.message
          : `Could not open ${desktopProvider} sign-in.`,
      );
    } finally {
      setLaunchingDesktopAuth(false);
    }
  }, [desktopProvider, loginApple, loginGoogle, loginMicrosoft, sessionId]);

  useEffect(() => {
    if (!isDesktopProviderFlow || !sessionId || !desktopProvider) return;
    if (
      desktopAuthAttempt?.provider === desktopProvider ||
      desktopAuthLaunchAttempted.current
    ) {
      return;
    }

    desktopAuthLaunchAttempted.current = true;
    void launchDesktopProviderAuth();
  }, [
    desktopAuthAttempt,
    desktopProvider,
    isDesktopProviderFlow,
    launchDesktopProviderAuth,
    sessionId,
  ]);

  const runComplete = useCallback(async (id: string) => {
    if (
      completeOnce.current ||
      activeCompleteSessionRef.current === id
    ) {
      return;
    }
    const user = auth.currentUser;
    if (!user) return;

    const generation = completeRunGenerationRef.current + 1;
    completeRunGenerationRef.current = generation;
    activeCompleteSessionRef.current = id;
    const isCurrentRun = () =>
      completeRunGenerationRef.current === generation &&
      activeCompleteSessionRef.current === id;
    const deadline = Date.now() + DEVICE_PAIRING_TTL_MS + 120_000;
    const sleep = (ms: number) =>
      new Promise<void>((r) => {
        setTimeout(r, ms);
      });

    try {
      while (Date.now() < deadline) {
        if (completeOnce.current || !isCurrentRun()) return;

        const st = await fetchPairingStatus(id);
        if (!isCurrentRun()) return;

        if (st.status === "expired") {
          setPairError(
            "This code has expired. Request a new code from View R Studio.",
          );
          return;
        }

        if (st.status === "completed") {
          setPairError(
            "This pairing session already finished. Open a new link from View R Studio if needed.",
          );
          return;
        }

        if (st.status === "pending") {
          setPairError(null);
          await sleep(2000);
          continue;
        }

        if (st.status === "attached") {
          const token = await user.getIdToken();
          const res = await completePairingSession(
            id,
            token,
            user.email,
            user.displayName,
          );

          if (res.ok) {
            if (!isCurrentRun()) return;
            completeOnce.current = true;
            setPairComplete(true);
            setPairError(null);
            return;
          }

          if (res.status === 409) {
            await sleep(1500);
            continue;
          }

          const t = await res.text();
          setPairError(t || res.statusText);
          return;
        }

        await sleep(2000);
      }
      setPairError(
        "Timed out: enter the code on Vision Pro first. If you are not signed in on the web, sign in below.",
      );
    } catch (e) {
      setPairError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrentRun()) {
        activeCompleteSessionRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !isDesktopProviderFlow) {
        void runComplete(sessionId);
      }
    });
    return () => {
      unsub();
      if (activeCompleteSessionRef.current === sessionId) {
        cancelCompleteLoop();
      }
    };
  }, [
    sessionId,
    runComplete,
    cancelCompleteLoop,
    isDesktopProviderFlow,
  ]);

  const desktopRedirectMatches = Boolean(
    isDesktopProviderFlow &&
      desktopProvider &&
      desktopAuthAttempt?.provider === desktopProvider &&
      redirectSignInResult?.provider === desktopProvider &&
      redirectSignInResult.completedAt >= desktopAuthAttempt.startedAt &&
      auth.currentUser?.uid === redirectSignInResult.uid,
  );

  useEffect(() => {
    if (!desktopRedirectMatches || !sessionId) return;
    void runComplete(sessionId);
  }, [desktopRedirectMatches, runComplete, sessionId]);

  useEffect(() => {
    if (pairComplete && sessionId && isDesktopProviderFlow) {
      clearDesktopPairingAuthAttempt(sessionId);
    }
  }, [isDesktopProviderFlow, pairComplete, sessionId]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={designTokens.fill.background.grey20.dark}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasSessionParams) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        bgcolor={designTokens.fill.background.grey20.dark}
        sx={{
          pt: { xs: 6, sm: "92px" },
          pb: { xs: 6, sm: "88px" },
          px: { xs: 2, sm: "92px" },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 580,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "44px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <MainLogo />
            <Typography
              sx={{
                ...designTokens.typography.styles.display2,
                color: designTokens.fill.text.grey90.dark,
                textAlign: "center",
                mt: 3,
              }}
            >
              Welcome to
            </Typography>
            <Typography
              sx={{
                ...designTokens.typography.styles.display1,
                color: designTokens.fill.text.grey90.dark,
                textAlign: "center",
              }}
            >
              View R
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              gap: "48px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: 580,
                gap: designTokens.numbers.gap[32],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  gap: "28px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: designTokens.typography.fontFamily.pretendard,
                    fontSize: "20px",
                    fontWeight: 400,
                    lineHeight: 1.2,
                    letterSpacing: "-0.2px",
                    color: designTokens.fill.text.grey90.dark,
                    textAlign: "center",
                  }}
                >
                  Sign in with a code from View R Studio
                </Typography>

                <SocialButton
                  customIcon={
                    <VpnKeyOutlinedIcon
                      sx={{ fontSize: 24, color: designTokens.fill.text.common100.dark }}
                    />
                  }
                  onClick={() =>
                    navigate("/login/studio-code")
                  }
                >
                  Continue with Studio Code
                </SocialButton>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    width: "100%",
                    maxWidth: 467,
                    justifyContent: "center",
                  }}
                >
                  <Box sx={dividerLineSx} />
                  <Typography
                    sx={{
                      fontFamily: designTokens.typography.fontFamily.pretendard,
                      fontSize: "20px",
                      fontWeight: 500,
                      color: designTokens.fill.text.grey80.dark,
                    }}
                  >
                    or
                  </Typography>
                  <Box sx={dividerLineSx} />
                </Box>
              </Box>

              <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
                <SocialButton
                  onClick={() => void loginGoogle()}
                  src="/Images/Icons/Google.png"
                  alt="Google"
                >
                  Continue with Google
                </SocialButton>
                <SocialButton
                  onClick={() => void loginMicrosoft()}
                  src="/Images/Icons/Microsoft.png"
                  alt="Microsoft"
                >
                  Continue with Microsoft
                </SocialButton>
                <SocialButton
                  onClick={() => void loginApple()}
                  src="/Images/Icons/Apple.png"
                  darkSrc="/Images/Icons/Social Login_apple_dark.png"
                  lightSrc="/Images/Icons/Social Login_apple_light.png"
                  alt="Apple"
                >
                  Continue with Apple
                </SocialButton>
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="text"
                  onClick={() => setOpenTerms(true)}
                  sx={{
                    ...designTokens.typography.styles.viewrStudio.headline2Regular,
                    color: designTokens.fill.text.grey80.dark,
                    textTransform: "none",
                    textDecoration: "underline",
                    minWidth: 0,
                    p: 0,
                  }}
                >
                  Terms of Service
                </Button>
                <Typography
                  component="span"
                  sx={{
                    color: designTokens.fill.text.grey80.dark,
                    fontSize: "17px",
                  }}
                >
                  ·
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setOpenPrivacy(true)}
                  sx={{
                    ...designTokens.typography.styles.viewrStudio.headline2Regular,
                    color: designTokens.fill.text.grey80.dark,
                    textTransform: "none",
                    textDecoration: "underline",
                    minWidth: 0,
                    p: 0,
                  }}
                >
                  Privacy Policy
                </Button>
              </Box>
            </Box>

            <Button
              variant="text"
              onClick={() => void loginGuest()}
              sx={{
                fontFamily: designTokens.typography.fontFamily.pretendard,
                fontSize: "20px",
                fontWeight: 400,
                lineHeight: "23px",
                color: designTokens.fill.text.common100.dark,
                textTransform: "none",
                borderRadius: "12px",
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: designTokens.colors.opacity.white[10],
                },
              }}
            >
              Get started as a guest
            </Button>
          </Box>
        </Box>

        <Terms open={openTerms} onClose={() => setOpenTerms(false)} />
        <Privacy open={openPrivacy} onClose={() => setOpenPrivacy(false)} />
      </Box>
    );
  }

  if (isDesktopProviderFlow && desktopProvider) {
    const providerName =
      desktopProvider === "google"
        ? "Google"
        : desktopProvider === "microsoft"
          ? "Microsoft"
          : "Apple";
    const providerIcon =
      desktopProvider === "google"
        ? "/Images/Icons/Google.png"
        : desktopProvider === "microsoft"
          ? "/Images/Icons/Microsoft.png"
          : "/Images/Icons/Apple.png";
    const redirectMismatch = Boolean(
      desktopAuthAttempt &&
        redirectSignInResult &&
        redirectSignInResult.completedAt >= desktopAuthAttempt.startedAt &&
        !desktopRedirectMatches,
    );

    return (
      <Box
        minHeight="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          px: 2,
          py: 6,
          bgcolor: designTokens.fill.background.grey20.dark,
        }}
      >
        <MainLogo />
        <Typography
          sx={{
            ...designTokens.typography.styles.display2,
            color: designTokens.fill.text.grey90.dark,
            textAlign: "center",
            mt: 3,
          }}
        >
          Sign in with {providerName}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 467, mt: 1, mb: 4 }}
        >
          Continue in your browser to sign in to View R Studio.
        </Typography>

        {launchingDesktopAuth || (desktopRedirectMatches && !pairComplete) ? (
          <Stack spacing={2} alignItems="center">
            <CircularProgress aria-label={`${providerName} sign-in in progress`} />
            <Typography variant="body2" color="text.secondary">
              {desktopRedirectMatches
                ? "Finishing sign-in…"
                : `Opening ${providerName} sign-in…`}
            </Typography>
          </Stack>
        ) : (
          <SocialButton
            onClick={() => void launchDesktopProviderAuth()}
            src={providerIcon}
            darkSrc={
              desktopProvider === "apple"
                ? "/Images/Icons/Social Login_apple_dark.png"
                : undefined
            }
            lightSrc={
              desktopProvider === "apple"
                ? "/Images/Icons/Social Login_apple_light.png"
                : undefined
            }
            alt={providerName}
          >
            Continue with {providerName}
          </SocialButton>
        )}

        {pairComplete && (
          <Alert severity="success" sx={{ mt: 3, maxWidth: 467 }}>
            Signed in. You can return to View R Studio.
          </Alert>
        )}
        {(pairError || authError || redirectMismatch) && !pairComplete && (
          <Alert severity="warning" sx={{ mt: 3, maxWidth: 467 }}>
            {pairError ||
              authError ||
              `The completed sign-in did not match ${providerName}. Please try again.`}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{
        pt: { xs: 4, sm: 6 },
        px: 2,
        pb: 4,
        bgcolor: designTokens.fill.background.grey20.dark,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 580, display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Button
          variant="text"
          color="inherit"
          onClick={() => navigate("/pair", { replace: true })}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          ← Back
        </Button>
      </Box>
      <MainLogo />
      <Typography
        sx={{
          ...designTokens.typography.styles.display2,
          color: designTokens.fill.text.grey90.dark,
          textAlign: "center",
          mt: 2,
        }}
      >
        Connect session
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={1} sx={{ maxWidth: 467 }}>
        {isAuthenticated
          ? "You’re signed in. Enter this code on Vision Pro. No need to sign in again."
          : "Enter this code on your headset. Sign in below to link this session to your account."}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={2}>
        Code is 6 characters: 3 letters (A–Z, no O/I/L) and 3 digits (2–9), order shuffled.
        The same character or digit appears at most twice (e.g. 662233-style OK; triples like 666222
        are not generated).
      </Typography>

      {shortCode && (
        <Typography
          variant="h3"
          letterSpacing={2}
          sx={{
            fontFamily: "monospace",
            mb: 2,
            color: designTokens.fill.text.grey90.dark,
          }}
        >
          {shortCode}
        </Typography>
      )}

      {!isAuthenticated && (
        <Stack spacing={2} width="100%" maxWidth={467} mb={2} alignItems="center">
          <SocialButton
            onClick={() => void loginGoogle()}
            src="/Images/Icons/Google.png"
            alt="Google"
          >
            Continue with Google
          </SocialButton>
          <SocialButton
            onClick={() => void loginMicrosoft()}
            src="/Images/Icons/Microsoft.png"
            alt="Microsoft"
          >
            Continue with Microsoft
          </SocialButton>
          <SocialButton
            onClick={() => void loginApple()}
            src="/Images/Icons/Apple.png"
            darkSrc="/Images/Icons/Social Login_apple_dark.png"
            lightSrc="/Images/Icons/Social Login_apple_light.png"
            alt="Apple"
          >
            Continue with Apple
          </SocialButton>
        </Stack>
      )}

      {pairComplete && (
        <Alert severity="success" sx={{ mt: 2, maxWidth: 400 }}>
          Headset linked. You can return to Vision Pro.
        </Alert>
      )}
      {pairError && (
        <Alert severity="warning" sx={{ mt: 2, maxWidth: 400 }}>
          {pairError}
        </Alert>
      )}
    </Box>
  );
};

export default PairPage;
